/**
 * OrderDetailModal — Admin order detail view with lifecycle action buttons.
 *
 * @see US5, AC-18, AC-19 in docs/specifications/2026-03-08-billing-admin-ui.md
 * @see AC-1 through AC-9 in docs/specifications/2026-04-22-admin-order-creation-ui/spec.md
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ManualPricingModal } from "./ManualPricingModal";
import { ReversePaymentModal } from "./ReversePaymentModal";
import { GenerateProformaModal } from "./GenerateProformaModal";
import { IssueStripeLinkButton } from "./IssueStripeLinkButton";
import { OrderStatusBadge } from "../../../../components/billing/OrderStatusBadge";
import type { OrderStatus } from "../../../../components/billing/OrderStatusBadge";
import type { BillingPayment } from "../../../../types/adminBilling";
import { useHotel } from "../../../../contexts/HotelContext";
import {
  useBillingOrderQuery,
  useCancelBillingOrderMutation,
  useRegisterBillingPaymentMutation,
  useConfirmBillingOrderMutation,
  usePriceBillingOrderMutation,
} from "../../../../hooks/useAdminProductCatalog";
import { useToast } from "../../../../contexts/ToastContext";
import {
  formatCents,
  formatDate,
  todayIso,
  centsToPlnString,
  plnToCents,
  DOC_TYPE_LABELS,
  DOC_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PRICING_TYPE_LABELS,
} from "../utils/orderFormatters";
import {
  getPricingLabel,
  type OrderLinePricingType,
} from "../../../../types/billing-labels";
import {
  findIssuedProforma,
  hasAvailableActions,
  getAvailableActions,
} from "../utils/orderActions";

// =============================================================================
// PROPS
// =============================================================================

export interface OrderDetailModalProps {
  orderId: string;
  /**
   * Brand UUID for IDOR-protected GET /admin/billing/orders/:id.
   * Required (H-SEC-1) — passed as query param to backend BrandQueryDto.
   */
  brandId: string;
  onClose: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  orderId,
  brandId: brandIdProp,
  onClose,
}) => {
  const { data: order, isLoading, isError } = useBillingOrderQuery(
    orderId,
    brandIdProp,
  );
  const { addToast } = useToast();
  const { user } = useHotel();
  const isAdmin = user?.role === "admin";
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalTitleId = `admin-order-detail-title-${orderId}`;

  // --- Form visibility state ---
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // --- Manual pricing modal state (AC-1, AC-2, AC-7) ---
  const [manualPricingLineId, setManualPricingLineId] = useState<string | null>(null);

  // --- Reverse payment modal state (AC-A4) ---
  const [reversePaymentTarget, setReversePaymentTarget] = useState<BillingPayment | null>(null);
  // Refs to per-line "Wyceń ręcznie" buttons — needed to return focus after modal closes
  const manualPricingTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Refs to per-payment "Cofnij płatność" buttons — focus returns here on modal close (WCAG 2.4.3)
  const reversePaymentTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // --- Generate proforma dialog state (AC-4) ---
  const [showProformaDialog, setShowProformaDialog] = useState(false);
  const proformaTriggerRef = useRef<HTMLButtonElement>(null);

  // --- Payment form state (AC-2, AC-7a) ---
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [paymentReference, setPaymentReference] = useState("");

  // --- Cancel form state (AC-4) ---
  const [cancelReason, setCancelReason] = useState("");

  // --- isFullyPaid local state (AC-8) — initialised from GET order response,
  //     updated immediately on register-payment success for instant feedback ---
  const [isFullyPaid, setIsFullyPaid] = useState(order?.isFullyPaid ?? false);

  // --- Auto-focus refs ---
  const paymentAmountInputRef = useRef<HTMLInputElement>(null);
  const cancelReasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // --- Mutation hooks ---
  // Prefer the prop (set by caller from tenant.brandId / selected brand) over
  // the loaded order's brandId — both should match, but the prop is available
  // before the GET resolves and is the authoritative source for IDOR check.
  const brandId = brandIdProp || order?.brandId || "";

  const cancelMutation = useCancelBillingOrderMutation(orderId, brandId);
  const paymentMutation = useRegisterBillingPaymentMutation(orderId, brandId);
  const confirmMutation = useConfirmBillingOrderMutation(orderId, brandId);
  const priceMutation = usePriceBillingOrderMutation(orderId, brandId);

  const isAnyMutationLoading =
    cancelMutation.isPending ||
    paymentMutation.isPending ||
    confirmMutation.isPending ||
    priceMutation.isPending;

  // Sync isFullyPaid from server when order data (re)loads (fixes modal-reopen bug)
  useEffect(() => {
    if (order?.isFullyPaid) {
      setIsFullyPaid(true);
    }
  }, [order?.isFullyPaid]);

  // Reset payment form defaults when order loads or payment form opens
  useEffect(() => {
    if (order && showPaymentForm) {
      setPaymentAmount(centsToPlnString(order.totalGrossCents));
      setPaymentDate(todayIso());
      setPaymentReference("");
    }
  }, [order, showPaymentForm]);

  // Auto-focus first input when payment form opens
  useEffect(() => {
    if (showPaymentForm) {
      paymentAmountInputRef.current?.focus();
    }
  }, [showPaymentForm]);

  // Auto-focus textarea when cancel form opens
  useEffect(() => {
    if (showCancelForm) {
      cancelReasonTextareaRef.current?.focus();
    }
  }, [showCancelForm]);

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showPaymentForm) {
          setShowPaymentForm(false);
        } else if (showCancelForm) {
          setShowCancelForm(false);
        } else if (showConfirmDialog) {
          setShowConfirmDialog(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, showPaymentForm, showCancelForm, showConfirmDialog]);

  // Focus close button on open (AC-1a)
  useEffect(() => {
    if (!isLoading && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isLoading]);

  // Focus trap for Tab/Shift+Tab within modal (AC-1a)
  const handleTabTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const modal = e.currentTarget;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // --- Payment form submit handler (AC-2) ---
  const handlePaymentSubmit = async () => {
    if (!order) return;

    const proforma = findIssuedProforma(order);
    if (!proforma) return;

    const amountCents = plnToCents(paymentAmount);
    if (amountCents < 1 || isNaN(amountCents)) {
      addToast({ message: "Kwota musi wynosić co najmniej 0.01 PLN", type: "error" });
      return;
    }
    if (!paymentDate) {
      addToast({ message: "Data wpłaty jest wymagana", type: "error" });
      return;
    }
    if (paymentReference.length > 500) {
      addToast({ message: "Numer referencyjny nie może przekraczać 500 znaków", type: "error" });
      return;
    }

    try {
      const result = await paymentMutation.mutateAsync({
        amountCents,
        // Bank transfer only — Stripe payments are registered automatically via webhook
        source: "BANK_TRANSFER",
        receivedAt: paymentDate,
        documentId: proforma.id,
        externalReference: paymentReference || undefined,
      });

      if (result.isFullyPaid) {
        // Set local isFullyPaid flag (AC-8) — enables "Potwierdź i realizuj" button
        setIsFullyPaid(true);
      } else {
        // Partial payment toast (AC-2 edge case)
        const remainingCents = order.totalGrossCents - amountCents;
        addToast({
          message: `Płatność zarejestrowana. Zamówienie wymaga dopłaty ${formatCents(remainingCents)}`,
          type: "success",
        });
      }

      setShowPaymentForm(false);
    } catch {
      // Error toast already shown by mutation hook (AC-7)
    }
  };

  // --- Confirm handler (AC-3) ---
  const handleConfirmOrder = async () => {
    try {
      await confirmMutation.mutateAsync();
      setShowConfirmDialog(false);
    } catch {
      // Error toast already shown by mutation hook (AC-7)
    }
  };

  // --- Cancel handler (AC-4) ---
  const handleCancelSubmit = async () => {
    if (cancelReason.length < 10) {
      addToast({ message: "Powód anulowania musi zawierać co najmniej 10 znaków", type: "error" });
      return;
    }
    if (cancelReason.length > 1000) {
      addToast({ message: "Powód anulowania nie może przekraczać 1000 znaków", type: "error" });
      return;
    }

    try {
      await cancelMutation.mutateAsync(cancelReason);
      setShowCancelForm(false);
      setCancelReason("");
    } catch {
      // Error toast already shown by mutation hook (AC-7)
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-3">
          <div
            role="status"
            aria-label="Ładowanie szczegółów zamówienia"
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
          >
            {/* Visible sr-only fallback for assistive tech that doesn't surface aria-label
                on role="status" elements without text children (UX LOW). */}
            <span className="sr-only">Ładowanie...</span>
          </div>
          <p className="text-sm text-neutral-600">Ładowanie...</p>
        </div>
      </div>
    );
  }

  // AC-1b: Error state with role="alert" instead of returning null
  if (!order || isError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center" role="alert">
          <p className="text-red-600">Nie udało się załadować szczegółów zamówienia.</p>
          <button onClick={onClose} className="mt-4 text-primary-700 underline">
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  // Derived state for action bar visibility
  const showActionBar = hasAvailableActions(order);
  const issuedProforma = findIssuedProforma(order);
  const isZeroPln = order.totalGrossCents === 0;

  const {
    showPriceButton,
    showProformaButton,
    showPaymentButton,
    showConfirmButton,
    showCancelButton,
  } = getAvailableActions(order, isFullyPaid);

  // Payment amount indicator
  const enteredAmountCents = plnToCents(paymentAmount || "0");
  const isPartialPayment =
    !isNaN(enteredAmountCents) &&
    enteredAmountCents > 0 &&
    enteredAmountCents < order.totalGrossCents;
  const isFullPayment =
    !isNaN(enteredAmountCents) && enteredAmountCents >= order.totalGrossCents;

  // Active line for manual pricing (needed to pass currentPricing to modal)
  const manualPricingLine = manualPricingLineId
    ? order.lines?.find((l) => l.id === manualPricingLineId)
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        onKeyDown={handleTabTrap}
        className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3
              id={modalTitleId}
              className="text-lg font-semibold text-neutral-900"
            >
              Zamówienie #{order.orderNumber}
            </h3>
            <span className="text-xs font-mono text-neutral-500">{order.id}</span>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <div className="p-6 space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-md">
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Tenant
                </span>
                {order.tenantName && (
                  <p className="text-sm text-neutral-800 mt-0.5 font-medium">{order.tenantName}</p>
                )}
                <p className="text-xs font-mono text-neutral-500 mt-0.5">{order.tenantId}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Status
                </span>
                <div className="mt-1">
                  <OrderStatusBadge status={order.status as OrderStatus} />
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Metoda płatności
                </span>
                <p className="text-sm text-neutral-800 mt-0.5">
                  {(order.paymentMethod && PAYMENT_METHOD_LABELS[order.paymentMethod]) ?? order.paymentMethod ?? "—"}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Kwota netto
                </span>
                <p className="text-sm font-medium text-neutral-800 mt-0.5">
                  {formatCents(order.totalNetCents)}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Kwota brutto
                </span>
                <p className="text-sm font-medium text-neutral-800 mt-0.5">
                  {formatCents(order.totalGrossCents)}
                </p>
              </div>
              {order.vatRate !== undefined && order.vatRate !== null && (
                <div>
                  <span className="text-xs font-medium text-neutral-500 uppercase">
                    VAT
                  </span>
                  <p className="text-sm text-neutral-800 mt-0.5">
                    {order.vatRate}%
                  </p>
                </div>
              )}
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Waluta
                </span>
                <p className="text-sm text-neutral-800 mt-0.5">
                  {order.currency ?? "PLN"}
                </p>
              </div>
              {order.paidAt && (
                <div>
                  <span className="text-xs font-medium text-neutral-500 uppercase">
                    Opłacono
                  </span>
                  <p className="text-sm text-neutral-800 mt-0.5">
                    {formatDate(order.paidAt)}
                  </p>
                </div>
              )}
            </div>

            {/* Cancel reason banner */}
            {order.status === "CANCELED" && order.cancelReason && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-xs font-medium text-red-800 uppercase mb-1">
                  Powód anulowania
                </p>
                <p className="text-sm text-red-700 whitespace-pre-wrap">{order.cancelReason}</p>
                {order.canceledAt && (
                  <p className="text-xs text-red-500 mt-2">
                    Anulowano: {formatDate(order.canceledAt)}
                  </p>
                )}
              </div>
            )}

            {/* Order Lines */}
            {order.lines && order.lines.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                  Pozycje zamówienia
                </h4>
                <div className="space-y-2">
                  {order.lines.map((line) => (
                    <div
                      key={line.id}
                      className="bg-neutral-50 border border-neutral-200 rounded-md p-3"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-800">
                            {line.description}
                          </p>
                          {(line.periodStart || line.periodEnd) && (
                            <p className="text-xs text-neutral-500 mt-0.5">
                              Okres: {formatDate(line.periodStart)} —{" "}
                              {formatDate(line.periodEnd)}
                            </p>
                          )}
                          {/* AC-5: arbitraryReason inline in expanded row */}
                          {line.pricing?.type === "ARBITRARY" && line.pricing.arbitraryReason && (
                            <p className="text-xs text-neutral-600 mt-1 italic">
                              Powód: {line.pricing.arbitraryReason}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {line.pricing && (
                            <div className="text-right">
                              {line.pricing.netCents != null && (
                                <p className="text-sm font-medium text-neutral-800">
                                  {formatCents(line.pricing.netCents)}{" "}
                                  <span className="text-xs font-normal text-neutral-500">netto</span>
                                </p>
                              )}
                              {line.pricing.grossCents != null && (
                                <p className="text-xs text-neutral-500">
                                  brutto: {formatCents(line.pricing.grossCents)}
                                </p>
                              )}
                              {/* AC-5: badge "Cena indywidualna" with tooltip for ARBITRARY */}
                              {line.pricing.type === "ARBITRARY" ? (
                                <span
                                  className="inline-block text-xs bg-purple-100 text-purple-800 font-medium px-2 py-0.5 rounded-full mt-0.5 cursor-help"
                                  title={line.pricing.arbitraryReason ?? undefined}
                                >
                                  {PRICING_TYPE_LABELS["ARBITRARY"]}
                                </span>
                              ) : (
                                <p className="text-xs text-neutral-400">
                                  {getPricingLabel(line.pricing.type)}
                                </p>
                              )}
                            </div>
                          )}
                          {/* AC-1, AC-7: per-line "Wyceń ręcznie" button — only visible in DRAFT */}
                          {order.status === "DRAFT" && (
                            <button
                              ref={(el) => {
                                manualPricingTriggerRefs.current[line.id] = el;
                              }}
                              type="button"
                              onClick={() => setManualPricingLineId(line.id)}
                              disabled={isAnyMutationLoading}
                              className="text-xs px-2 py-1 border border-neutral-300 rounded-md text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
                            >
                              Wyceń ręcznie
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {order.documents && order.documents.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                  Dokumenty
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Typ
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Numer
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Kwota brutto
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Wystawiony
                        </th>
                        {/* M1: the manually set due date is otherwise only visible
                            in a transient toast and inside the PDF. */}
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Termin płatności
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                          Akcje
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      {order.documents.map((doc) => (
                        <tr key={doc.id} className="hover:bg-neutral-50">
                          <td className="px-3 py-2 text-xs font-medium">
                            {DOC_TYPE_LABELS[doc.type] || doc.type}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono">
                            {doc.documentNumber}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {DOC_STATUS_LABELS[doc.status] || doc.status}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {formatCents(doc.amountGrossCents)}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {formatDate(doc.issuedAt)}
                          </td>
                          <td
                            className="px-3 py-2 text-xs"
                            data-testid={`document-due-date-${doc.id}`}
                          >
                            {formatDate(doc.dueDate)}
                          </td>
                          <td className="px-3 py-2 text-right text-xs space-x-2">
                            {doc.viewUrl && (
                              <a
                                href={doc.viewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800"
                              >
                                Podgląd
                              </a>
                            )}
                            {doc.pdfUrl && (
                              <a
                                href={doc.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800"
                              >
                                PDF
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payments (AC-A4, AC-X2) */}
            {order.payments && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                  Płatności
                </h4>
                {order.payments.length === 0 ? (
                  <p className="text-sm text-neutral-500 italic">
                    Brak płatności dla tego zamówienia.
                  </p>
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Data
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Kwota
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Źródło
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Status
                        </th>
                        {isAdmin && (
                          <th className="px-3 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                            Akcje
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      {order.payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-neutral-50">
                          <td className="px-3 py-2 text-xs">
                            {formatDate(payment.receivedAt)}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <div>
                              {formatCents(payment.amountCents)}
                            </div>
                            {/* AC-X2: processingFeeCents display — only for STRIPE source
                                (BANK_TRANSFER may carry an irrelevant fee value that must
                                not be labeled "Prowizja Stripe"). */}
                            {payment.source === "STRIPE" &&
                              payment.processingFeeCents != null &&
                              payment.processingFeeCents > 0 && (
                                <div className="text-neutral-500 text-xs">
                                  Prowizja Stripe: {formatCents(payment.processingFeeCents)}
                                </div>
                              )}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {PAYMENT_METHOD_LABELS[payment.source] ?? payment.source}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {/* H-18: REVERSED badge should be gray.
                                NOTE (LOW UX): English status codes (RECORDED/REVERSED) are
                                intentionally NOT localized — they are technical/audit
                                identifiers shown only on the admin panel, mirror the DB
                                enum value (`billing.payments.status`), and match what
                                appears in audit logs / Loki dumps. Localizing would force
                                a back-translation step during incident triage. */}
                            {payment.status === "REVERSED" ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                                REVERSED
                              </span>
                            ) : payment.status === "RECORDED" ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                RECORDED
                              </span>
                            ) : (
                              <span className="text-neutral-600">{payment.status}</span>
                            )}
                          </td>
                          {/* AC-A4: "Cofnij płatność" button — admin only, RECORDED only (H-18) */}
                          {isAdmin && (
                            <td className="px-3 py-2 text-right text-xs">
                              {payment.status === "RECORDED" && (
                                <button
                                  ref={(el) => {
                                    reversePaymentTriggerRefs.current[payment.id] = el;
                                  }}
                                  type="button"
                                  onClick={() => setReversePaymentTarget(payment)}
                                  aria-label={`Cofnij płatność z dnia ${formatDate(payment.receivedAt)} na kwotę ${formatCents(payment.amountCents)}`}
                                  className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                                >
                                  Cofnij płatność
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Parties */}
            {order.parties && order.parties.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                  Strony
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Rola
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Typ
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                          Party ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      {order.parties.map((party) => (
                        <tr key={party.id} className="hover:bg-neutral-50">
                          <td className="px-3 py-2 text-xs">{party.role}</td>
                          <td className="px-3 py-2 text-xs">{party.partyType}</td>
                          <td className="px-3 py-2 text-xs font-mono">
                            {party.partyId}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* INLINE FORMS (shown below content, above sticky bar)          */}
            {/* ============================================================= */}

            {/* Payment Registration Form (AC-2, AC-2a, AC-7a) */}
            {showPaymentForm && (
              <div className="border border-primary-200 bg-primary-50 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-semibold text-neutral-900">
                  Zaksięguj płatność
                </h4>

                {/* Previous payments info */}
                {order.paidAt && (
                  <p className="text-xs text-neutral-600">
                    Dotychczas zaksięgowano: {formatCents(order.totalGrossCents)} PLN
                  </p>
                )}

                {/* Amount field */}
                <div>
                  <label
                    htmlFor="payment-amount"
                    className="block text-xs font-medium text-neutral-700 mb-1"
                  >
                    Kwota (PLN)
                  </label>
                  <input
                    ref={paymentAmountInputRef}
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {/* Inline amount indicator (AC-2 edge case) */}
                  {paymentAmount && (
                    <p
                      className={`text-xs mt-1 font-medium ${
                        isPartialPayment
                          ? "text-yellow-600"
                          : isFullPayment
                            ? "text-green-600"
                            : "text-neutral-500"
                      }`}
                    >
                      {isPartialPayment
                        ? "Częściowa wpłata"
                        : isFullPayment
                          ? "Pełna wpłata"
                          : ""}
                    </p>
                  )}
                </div>

                {/* Date field */}
                <div>
                  <label
                    htmlFor="payment-date"
                    className="block text-xs font-medium text-neutral-700 mb-1"
                  >
                    Data wpłaty
                  </label>
                  <input
                    id="payment-date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* External reference field */}
                <div>
                  <label
                    htmlFor="payment-reference"
                    className="block text-xs font-medium text-neutral-700 mb-1"
                  >
                    Numer referencyjny (opcjonalny)
                  </label>
                  <input
                    id="payment-reference"
                    type="text"
                    maxLength={500}
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="np. numer przelewu"
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {paymentReference.length > 0 && (
                    <p className="text-xs text-neutral-400 mt-0.5 text-right">
                      {paymentReference.length}/500
                    </p>
                  )}
                </div>

                {/* Form actions */}
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPaymentForm(false)}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Anuluj
                  </button>
                  <button
                    type="button"
                    onClick={handlePaymentSubmit}
                    disabled={isAnyMutationLoading || !paymentAmount || !paymentDate}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {paymentMutation.isPending && (
                      <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                    )}
                    Zarejestruj płatność
                  </button>
                </div>
              </div>
            )}

            {/* Cancel Order Form (AC-4) */}
            {showCancelForm && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-semibold text-red-900">
                  Anuluj zamówienie
                </h4>
                <p className="text-xs text-red-700">
                  Zamówienie zostanie trwale anulowane. Aktywna subskrypcja nie
                  zostanie zmieniona.
                </p>

                {/* Reason textarea */}
                <div>
                  <label
                    htmlFor="cancel-reason"
                    className="block text-xs font-medium text-neutral-700 mb-1"
                  >
                    Powód anulowania
                  </label>
                  <textarea
                    ref={cancelReasonTextareaRef}
                    id="cancel-reason"
                    rows={3}
                    minLength={10}
                    maxLength={1000}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Podaj powód anulowania (min. 10 znaków)"
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  <p
                    className={`text-xs mt-0.5 text-right ${
                      cancelReason.length > 0 && cancelReason.length < 10
                        ? "text-red-500"
                        : "text-neutral-400"
                    }`}
                  >
                    {cancelReason.length < 10
                      ? `${cancelReason.length}/10 (minimum)`
                      : `${cancelReason.length}/1000`}
                  </p>
                </div>

                {/* Form actions */}
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCancelForm(false);
                      setCancelReason("");
                    }}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Cofnij
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelSubmit}
                    disabled={
                      isAnyMutationLoading ||
                      cancelReason.length < 10 ||
                      cancelReason.length > 1000
                    }
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {cancelMutation.isPending && (
                      <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                    )}
                    Potwierdź anulowanie
                  </button>
                </div>
              </div>
            )}

            {/* Confirm Dialog (AC-3, AC-8) */}
            {showConfirmDialog && (
              <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-semibold text-green-900">
                  Potwierdź i realizuj zamówienie
                </h4>
                <p className="text-sm text-green-800">
                  Czy na pewno chcesz potwierdzić i zrealizować to zamówienie?
                </p>
                <p className="text-xs text-green-700">
                  Aktywuje subskrypcję trial bez rejestrowania płatności.
                </p>

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowConfirmDialog(false)}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Anuluj
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmOrder}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {confirmMutation.isPending && (
                      <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                    )}
                    Potwierdź i realizuj
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ============================================================= */}
          {/* STICKY ACTION BAR (AC-1)                                      */}
          {/* ============================================================= */}
          {showActionBar && (
            <div className="sticky bottom-0 bg-white border-t border-neutral-200 px-6 py-3 flex items-center justify-between gap-3 flex-shrink-0">
              <div>
                {/* Cancel button — left side (AC-4) */}
                {showCancelButton && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCancelForm(true);
                      setShowPaymentForm(false);
                      setShowConfirmDialog(false);
                    }}
                    disabled={isAnyMutationLoading || showCancelForm}
                    className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    {cancelMutation.isPending && (
                      <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"
                        aria-hidden="true"
                      />
                    )}
                    Anuluj zamówienie
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {/* AC-6: Price order button */}
                {showPriceButton && (
                  <button
                    type="button"
                    onClick={() => priceMutation.mutate()}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {priceMutation.isPending && (
                      <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                    )}
                    Wyceń automatycznie
                  </button>
                )}

                {/* AC-7, AC-4: Generate proforma button — opens due-date dialog */}
                {showProformaButton && (
                  <button
                    type="button"
                    ref={proformaTriggerRef}
                    onClick={() => setShowProformaDialog(true)}
                    disabled={isAnyMutationLoading}
                    className="px-4 py-2 text-sm bg-primary-700 text-white rounded-md hover:bg-primary-800 disabled:opacity-50 flex items-center gap-2"
                  >
                    Generuj proformę
                  </button>
                )}

                {/* AC-B1: Issue Stripe Payment Link — only for DRAFT + PROFORMA */}
                {order.status === "DRAFT" && (
                  <IssueStripeLinkButton
                    orderId={orderId}
                    brandId={brandId}
                    orderStatus={order.status}
                    hasProforma={!!issuedProforma}
                  />
                )}

                {/* Register payment button (AC-2) — H9: aria-disabled pattern */}
                {showPaymentButton && (
                  <>
                    <button
                      type="button"
                      aria-disabled={!issuedProforma || undefined}
                      onClick={(e) => {
                        if (!issuedProforma) {
                          e.preventDefault();
                          return;
                        }
                        setShowPaymentForm(true);
                        setShowCancelForm(false);
                        setShowConfirmDialog(false);
                      }}
                      aria-describedby={!issuedProforma ? "hint-payment-proforma" : undefined}
                      className={`px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2 ${
                        isAnyMutationLoading || showPaymentForm ? "opacity-50 cursor-not-allowed" : ""
                      } ${!issuedProforma ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {paymentMutation.isPending && (
                        <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                      )}
                      Zaksięguj płatność
                    </button>
                    {!issuedProforma && (
                      <span id="hint-payment-proforma" className="sr-only">
                        Brak proformy — wygeneruj proformę przed rejestracją płatności
                      </span>
                    )}
                  </>
                )}

                {/* AC-8: Confirm button — 0 PLN or fully paid — H3: tooltip for session-based state */}
                {showConfirmButton && (
                  <>
                    <button
                      type="button"
                      aria-disabled={showConfirmDialog || undefined}
                      onClick={(e) => {
                        if (showConfirmDialog) {
                          e.preventDefault();
                          return;
                        }
                        setShowConfirmDialog(true);
                        setShowPaymentForm(false);
                        setShowCancelForm(false);
                      }}
                      title={
                        !isZeroPln && isFullyPaid
                          ? "Płatność zarejestrowana w tej sesji. Po zamknięciu okna przycisk wymaga ponownej rejestracji płatności."
                          : isZeroPln
                          ? "Aktywuje subskrypcję bez rejestrowania płatności"
                          : undefined
                      }
                      aria-describedby={isAnyMutationLoading ? "hint-confirm-loading" : undefined}
                      className={`px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 ${
                        isAnyMutationLoading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {confirmMutation.isPending && (
                        <span
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                        aria-hidden="true"
                      />
                      )}
                      Potwierdź i realizuj
                    </button>
                    {isAnyMutationLoading && (
                      <span id="hint-confirm-loading" className="sr-only">
                        Trwa operacja — poczekaj na zakończenie
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AC-2, AC-7: per-line ManualPricingModal */}
      {manualPricingLineId && (
        <ManualPricingModal
          orderId={orderId}
          brandId={brandId}
          lineId={manualPricingLineId}
          currentPricing={
            manualPricingLine?.pricing
              ? {
                  type: manualPricingLine.pricing.type as OrderLinePricingType,
                  netCents: manualPricingLine.pricing.netCents,
                }
              : null
          }
          onClose={() => setManualPricingLineId(null)}
          triggerRef={{
            current: manualPricingTriggerRefs.current[manualPricingLineId] ?? null,
          }}
        />
      )}

      {/* AC-4: Generate proforma due-date dialog */}
      {showProformaDialog && (
        <GenerateProformaModal
          orderId={orderId}
          brandId={brandId}
          order={order}
          onClose={() => setShowProformaDialog(false)}
          triggerRef={proformaTriggerRef}
        />
      )}

      {/* AC-A4: Reverse payment modal (admin only) */}
      {reversePaymentTarget && (
        <ReversePaymentModal
          open={true}
          onClose={() => setReversePaymentTarget(null)}
          payment={reversePaymentTarget}
          orderId={orderId}
          brandId={brandId}
          isAdmin={isAdmin}
          orderStatus={order.status}
          triggerRef={{
            current: reversePaymentTriggerRefs.current[reversePaymentTarget.id] ?? null,
          }}
        />
      )}
    </div>
  );
};
